import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PencilLine } from 'lucide-react';
import { Tag } from '@/components/Chips';
import { GenericEditorPanes, type GenericPaneMode } from '@/components/try/GenericEditorPanes';
import { GenericEditorSidebar } from '@/components/try/GenericEditorSidebar';
import { MessagePicker } from '@/components/try/MessagePicker';
import { TryEditorForm } from '@/components/try/TryEditorForm';
import { TryEditorPanes, type PaneMode } from '@/components/try/TryEditorPanes';
import { freshInput } from '@/components/try/freshInput';
import type { FieldKey, Selection } from '@/components/try/types';
import { messageByShort } from '@/data/iso20022';
import { samplesForMessage } from '@/data/samples';
import { structuredBuilderFor } from '@/lib/editor/registry';
import { editorPairFor, editorPrimaryShort } from '@/lib/editorPairs';
import { SchemaForm } from '@/components/message/SchemaForm';
import { SCHEMA_MODELS } from '@/data/generated/schemaModels';
import { StructuredBuilderEditor } from '@/components/try/StructuredBuilderEditor';
import {
  buildPacs002,
  buildPacs008,
  type Pacs002Outcome,
  type PacsBuildInput,
} from '@/lib/pacsBuilder';
import {
  SIMPLE_SYNC,
  highlightSelectors,
  resolveXmlClick,
  type EditorFieldKey,
} from '@/lib/pacsFields';
import { pairVersionId, versionInputPatch } from '@/lib/editor/versioning';
import { canonicalId, messageIdFromPayload } from '@/lib/messageId';
import { useT } from '@/i18n';
import { NotFoundView } from './NotFoundView';

const PACS_BUILDER_SHORT = 'pacs.008';

/**
 * Interactive ISO 20022 editor panel used by the message workspace (edit mode)
 * and legacy /try redirects.
 */
export function MessageEditWorkspace({
  short,
  versionId,
  embedded = false,
  helpMode = false,
}: {
  short: string;
  /** Fully versioned catalog id (pacs.008.001.08.ch.02, pain.001.003.03, …). */
  versionId?: string;
  embedded?: boolean;
  helpMode?: boolean;
}) {
  const t = useT();
  const primaryShort = editorPrimaryShort(short);
  const message = messageByShort(primaryShort);
  const isPacsBuilder = primaryShort === PACS_BUILDER_SHORT;
  const resolvedVersionId = versionId ?? message?.id;

  if (!message) return <NotFoundView />;

  const structured = structuredBuilderFor(primaryShort);
  if (structured) {
    return (
      <StructuredBuilderEditor
        def={structured}
        versionId={resolvedVersionId}
        embedded={embedded}
        helpMode={helpMode}
      />
    );
  }

  if (isPacsBuilder) {
    return (
      <PacsBuilderEditor
        t={t}
        versionId={resolvedVersionId}
        embedded={embedded}
        helpMode={helpMode}
      />
    );
  }

  return (
    <GenericMessageEditor
      messageShort={primaryShort}
      message={message}
      versionId={resolvedVersionId}
      t={t}
      embedded={embedded}
      helpMode={helpMode}
    />
  );
}

/** @deprecated Prefer MessageEditWorkspace via /messages/:short?mode=edit */
export function TryEditorView() {
  const { short: routeShort } = useParams();
  const rawShort = routeShort?.replace(/-/g, '.') ?? PACS_BUILDER_SHORT;
  return <MessageEditWorkspace short={rawShort} />;
}

function PacsBuilderEditor({
  t,
  versionId,
  embedded,
  helpMode = false,
}: {
  t: ReturnType<typeof useT>;
  versionId?: string;
  embedded?: boolean;
  helpMode?: boolean;
}) {
  const formRef = useRef<HTMLElement>(null);
  const [input, setInput] = useState<PacsBuildInput>(() => {
    const base = freshInput();
    if (!versionId) return base;
    const pair = pairVersionId(PACS_BUILDER_SHORT, versionId);
    return { ...base, ...versionInputPatch(versionId, pair) };
  });
  const [outcome, setOutcome] = useState<Pacs002Outcome>('ACSC');
  const [rejectReason, setRejectReason] = useState('AB05');
  const [edited008, setEdited008] = useState<string | null>(null);
  const [paneMode, setPaneMode] = useState<PaneMode>('008');
  const [expert, setExpert] = useState(false);
  const [selection, setSelection] = useState<Selection | null>(null);

  useEffect(() => {
    if (!versionId) return;
    const pair = pairVersionId(PACS_BUILDER_SHORT, versionId);
    const patch = versionInputPatch(versionId, pair);
    setEdited008(null);
    setInput((prev) => ({ ...prev, ...patch }));
  }, [versionId]);

  const pacs008 = useMemo(() => buildPacs008(input), [input]);
  const pacs002 = useMemo(
    () => buildPacs002(input, outcome, rejectReason),
    [input, outcome, rejectReason],
  );

  const display008 = edited008 ?? pacs008;
  const selectedPaths = selection?.paths ?? [];

  useEffect(() => {
    if (selection?.source !== 'xml') return;
    const el = formRef.current?.querySelector(`[data-field-key="${selection.fieldKey}"]`);
    el?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    el?.querySelector<HTMLElement>('input, select, textarea, button')?.focus();
  }, [selection, expert]);

  function patch<K extends FieldKey>(key: K, value: PacsBuildInput[K]) {
    setEdited008(null);
    setInput((prev) => {
      const next: PacsBuildInput = { ...prev, [key]: value };
      if (key === 'instant') {
        next.lclInstrm = value ? prev.lclInstrm.trim() || 'INST' : '';
      }
      if (key === 'lclInstrm') {
        next.instant = String(value).trim().toUpperCase() === 'INST';
      }
      if (key === 'msgId' && !expert) {
        next.statusMsgId = String(value).replace(/PACS8/i, 'PACS2');
      }
      if (!expert) {
        for (const mirror of SIMPLE_SYNC[key] ?? []) {
          (next[mirror] as PacsBuildInput[typeof mirror]) = value as never;
        }
      }
      return next;
    });
  }

  function activateField(key: string, source: 'form' | 'xml') {
    setSelection((prev) =>
      prev?.fieldKey === key && prev.source === 'xml' && source === 'form'
        ? prev
        : { fieldKey: key, source, paths: highlightSelectors(key as EditorFieldKey) },
    );
  }

  function selectFromXml(selector: string) {
    const hit = resolveXmlClick(selector, expert);
    if (!hit) {
      setSelection(null);
      return;
    }
    if (hit.enableExpert) setExpert(true);
    setSelection({
      fieldKey: hit.field.key,
      source: 'xml',
      paths: [...new Set([selector, ...hit.field.xmlSelectors])],
    });
  }

  function reset() {
    const base = freshInput();
    if (versionId) {
      const pair = pairVersionId(PACS_BUILDER_SHORT, versionId);
      setInput({ ...base, ...versionInputPatch(versionId, pair) });
    } else {
      setInput(base);
    }
    setOutcome('ACSC');
    setRejectReason('AB05');
    setEdited008(null);
    setSelection(null);
  }

  const hasSchema = Boolean(SCHEMA_MODELS[PACS_BUILDER_SHORT]);

  const body = (
    <>
      {!embedded && (
        <div className="max-w-xs">
          <MessagePicker short={PACS_BUILDER_SHORT} />
        </div>
      )}
      <div
        className={
          hasSchema
            ? 'grid gap-4 xl:grid-cols-[minmax(16rem,22%)_minmax(14rem,20%)_minmax(0,1fr)] xl:items-start'
            : 'grid gap-4 xl:grid-cols-[minmax(18rem,26%)_minmax(0,1fr)] xl:items-start'
        }
      >
        <TryEditorForm
          formRef={formRef}
          input={input}
          outcome={outcome}
          rejectReason={rejectReason}
          expert={expert}
          setExpert={setExpert}
          patch={patch}
          setOutcome={setOutcome}
          setRejectReason={setRejectReason}
          highlighted={selection?.fieldKey ?? null}
          onActivate={activateField}
          onReset={reset}
          t={t}
        />
        {hasSchema && (
          <SchemaForm
            short={PACS_BUILDER_SHORT}
            xml={display008}
            onXmlChange={setEdited008}
            helpMode={helpMode}
            selectedPaths={selectedPaths}
            onSelectPath={selectFromXml}
          />
        )}
        <TryEditorPanes
          display008={display008}
          pacs002={pacs002}
          outcome={outcome}
          rejectReason={rejectReason}
          paneMode={paneMode}
          setPaneMode={setPaneMode}
          selectedPaths={selectedPaths}
          onSelectPath={selectFromXml}
          onEdit008={setEdited008}
          t={t}
        />
      </div>
    </>
  );

  if (embedded) {
    return <div className="flex flex-col gap-4">{body}</div>;
  }

  return (
    <div className="page-fluid flex flex-col gap-4">
      <TryEditorHeader short={PACS_BUILDER_SHORT} t={t} />
      {body}
    </div>
  );
}

function GenericMessageEditor({
  messageShort,
  message,
  versionId,
  t,
  embedded,
  helpMode = false,
}: {
  messageShort: string;
  message: NonNullable<ReturnType<typeof messageByShort>>;
  versionId?: string;
  t: ReturnType<typeof useT>;
  embedded?: boolean;
  helpMode?: boolean;
}) {
  const pairShort = editorPairFor(messageShort);
  const samples = useMemo(() => {
    const all = samplesForMessage(messageShort).filter((s) => !s.id.endsWith('-json'));
    if (!versionId) return all;
    const matching = all.filter((s) => {
      const id = messageIdFromPayload(s.content);
      return id !== null && canonicalId(id) === canonicalId(versionId);
    });
    return matching.length > 0 ? matching : all;
  }, [messageShort, versionId]);
  const pairSamples = useMemo(() => {
    if (!pairShort) return [];
    const all = samplesForMessage(pairShort).filter((s) => !s.id.endsWith('-json'));
    if (!versionId) return all;
    const pairId = pairVersionId(messageShort, versionId);
    if (!pairId) return all;
    const matching = all.filter((s) => {
      const id = messageIdFromPayload(s.content);
      return id !== null && canonicalId(id) === canonicalId(pairId);
    });
    return matching.length > 0 ? matching : all;
  }, [pairShort, messageShort, versionId]);

  const [activeSample, setActiveSample] = useState(0);
  const [editedPrimary, setEditedPrimary] = useState<string | null>(null);
  const [editedPair, setEditedPair] = useState<string | null>(null);
  const [paneMode, setPaneMode] = useState<GenericPaneMode>('primary');

  useEffect(() => {
    setActiveSample(0);
    setEditedPrimary(null);
    setEditedPair(null);
    setPaneMode('primary');
  }, [messageShort, pairShort, versionId]);

  const sample = samples[activeSample] ?? samples[0];
  const pairSample = pairSamples[0];
  const primaryContent = editedPrimary ?? sample?.content ?? '';
  const pairContent = editedPair ?? pairSample?.content;

  function reset() {
    setEditedPrimary(null);
    setEditedPair(null);
    setActiveSample(0);
  }

  const hasSchema = Boolean(SCHEMA_MODELS[messageShort]);

  const body = (
    <div
      className={
        hasSchema
          ? 'grid gap-4 xl:grid-cols-[minmax(14rem,18%)_minmax(16rem,24%)_minmax(0,1fr)] xl:items-start'
          : 'grid gap-4 xl:grid-cols-[minmax(18rem,26%)_minmax(0,1fr)] xl:items-start'
      }
    >
      <GenericEditorSidebar
        message={message}
        short={messageShort}
        samples={samples}
        activeSample={activeSample}
        onSelectSample={(i) => {
          setActiveSample(i);
          setEditedPrimary(null);
        }}
        onReset={reset}
        t={t}
        hidePicker={embedded}
      />
      {hasSchema && (
        <SchemaForm
          short={messageShort}
          xml={primaryContent}
          onXmlChange={setEditedPrimary}
          helpMode={helpMode}
        />
      )}
      <GenericEditorPanes
        primaryShort={messageShort}
        primaryContent={primaryContent}
        pairShort={pairShort}
        pairContent={pairContent}
        paneMode={paneMode}
        setPaneMode={setPaneMode}
        onEditPrimary={setEditedPrimary}
        onEditPair={pairShort ? setEditedPair : undefined}
        t={t}
      />
    </div>
  );

  if (embedded) {
    return <div className="flex flex-col gap-4">{body}</div>;
  }

  return (
    <div className="page-fluid flex flex-col gap-4">
      <TryEditorHeader short={messageShort} pairShort={pairShort} t={t} />
      {body}
    </div>
  );
}

function TryEditorHeader({
  short,
  pairShort,
  t,
}: {
  short: string;
  pairShort?: string;
  t: ReturnType<typeof useT>;
}) {
  const isPacs = short === PACS_BUILDER_SHORT;

  return (
    <header className="shrink-0">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 max-w-4xl">
          <p className="eyebrow inline-flex items-center gap-1.5">
            <PencilLine size={12} aria-hidden />
            {t('try.eyebrow')}
          </p>
          <h1 className="mt-1 text-2xl font-bold sm:text-3xl">
            {isPacs ? t('try.title') : t('try.titleGeneric', { short })}
          </h1>
          <p className="mt-1.5 text-[14px] leading-relaxed text-muted">
            {isPacs ? t('try.lead') : t('try.leadGeneric')}
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Tag>{short}</Tag>
          {pairShort && <Tag>{pairShort}</Tag>}
          <Tag>XML</Tag>
          <Tag>{isPacs ? t('try.tagBuilder') : t('try.tagSample')}</Tag>
        </div>
      </div>
    </header>
  );
}
